import React, { useEffect, useState } from 'react';
import { 
  Target, 
  Plus, 
  Trash2, 
  Edit2,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Circle,
  TrendingUp,
  Calendar,
  Clock,
  RefreshCw,
  AlertTriangle,
  Wrench,
  Flag
} from 'lucide-react';
import { goalsApi, strategicObjectivesApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { calculateGoalProgress } from '../lib/utils';
import { toast } from 'sonner';

const Goals = () => {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [fixingDates, setFixingDates] = useState(false);
  const [expandedGoal, setExpandedGoal] = useState(null);
  const [editingGoal, setEditingGoal] = useState(null);
  
  // Check if user has enterprise access
  const effectivePlan = user?.promo_plan || user?.plan || 'free_trial';
  const hasEnterpriseAccess = effectivePlan === 'enterprise' || user?.is_admin;
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'business',
    goal_type: 'strategic',
    key_results: [{ id: Date.now().toString(), title: '', target_value: 100, current_value: 0, unit: '%', due_date: '', recurring: '' }],
    due_date: '',
    priority_id: '',
  });

  useEffect(() => {
    loadGoals();
    if (hasEnterpriseAccess) {
      loadPriorities();
    }
  }, [hasEnterpriseAccess]);

  const loadGoals = async () => {
    try {
      const response = await goalsApi.getAll();
      setGoals(response.data || []);
    } catch (error) {
      console.error('Failed to load goals:', error);
      toast.error('Failed to load goals');
    } finally {
      setLoading(false);
    }
  };

  const loadPriorities = async () => {
    try {
      const response = await strategicObjectivesApi.getPriorities();
      setPriorities(response.data || []);
    } catch (error) {
      console.error('Failed to load priorities:', error);
    }
  };

  const handleFixDates = async () => {
    setFixingDates(true);
    try {
      const response = await goalsApi.fixDates();
      toast.success(`Fixed ${response.data.fixed_count} goal(s) with invalid dates`);
      await loadGoals();
    } catch (error) {
      console.error('Failed to fix dates:', error);
      toast.error('Failed to fix goal dates');
    } finally {
      setFixingDates(false);
    }
  };

  // Check if any goals have date warnings
  const goalsWithWarnings = goals.filter(g => g.date_warning);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'business',
      goal_type: 'strategic',
      key_results: [{ id: Date.now().toString(), title: '', target_value: 100, current_value: 0, unit: '%', due_date: '', recurring: '' }],
      due_date: '',
      priority_id: '',
    });
    setEditingGoal(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate goal due date is on or after the latest action item due date
    const actionItemDates = formData.key_results
      .filter(item => item.due_date)
      .map(item => new Date(item.due_date));
    
    if (actionItemDates.length > 0 && formData.due_date) {
      const latestActionDate = new Date(Math.max(...actionItemDates));
      const goalDueDate = new Date(formData.due_date);
      
      if (goalDueDate < latestActionDate) {
        const formattedLatest = latestActionDate.toLocaleDateString('en-US', { 
          month: 'short', day: 'numeric', year: 'numeric' 
        });
        toast.error(`Goal due date must be on or after the latest action item date (${formattedLatest})`);
        return;
      }
    }
    
    // Auto-set goal due date to latest action item date if not set
    if (!formData.due_date && actionItemDates.length > 0) {
      const latestActionDate = new Date(Math.max(...actionItemDates));
      formData.due_date = latestActionDate.toISOString().split('T')[0];
    }
    
    try {
      if (editingGoal) {
        await goalsApi.update(editingGoal.id, formData);
        toast.success('Goal updated!');
      } else {
        await goalsApi.create(formData);
        toast.success('Goal created!');
      }
      await loadGoals();
      setShowForm(false);
      resetForm();
    } catch (error) {
      toast.error(editingGoal ? 'Failed to update goal' : 'Failed to create goal');
    }
  };

  const handleDelete = async (goalId) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) return;
    
    try {
      await goalsApi.delete(goalId);
      setGoals(prev => prev.filter(g => g.id !== goalId));
      toast.success('Goal deleted');
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const handleStatusChange = async (goalId, status) => {
    try {
      await goalsApi.updateStatus(goalId, status);
      setGoals(prev => prev.map(g => g.id === goalId ? { ...g, status } : g));
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleActionItemUpdate = async (goalId, itemId, value) => {
    const numValue = parseFloat(value) || 0;
    try {
      await goalsApi.updateKeyResult(goalId, itemId, numValue);
      setGoals(prev => prev.map(g => {
        if (g.id === goalId) {
          return {
            ...g,
            key_results: g.key_results.map(item => 
              String(item.id) === String(itemId) ? { ...item, current_value: numValue } : item
            )
          };
        }
        return g;
      }));
      toast.success('Progress updated!');
    } catch (error) {
      toast.error('Failed to update action item');
    }
  };

  const addActionItem = () => {
    setFormData(prev => ({
      ...prev,
      key_results: [...prev.key_results, { 
        id: Date.now().toString(), 
        title: '', 
        target_value: 100, 
        current_value: 0, 
        unit: '%',
        due_date: '',
        recurring: ''
      }]
    }));
  };

  const removeActionItem = (index) => {
    setFormData(prev => ({
      ...prev,
      key_results: prev.key_results.filter((_, i) => i !== index)
    }));
  };

  const updateActionItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      key_results: prev.key_results.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const openEditForm = (goal) => {
    setFormData({
      title: goal.title,
      description: goal.description,
      category: goal.category,
      goal_type: goal.goal_type || 'strategic',
      key_results: goal.key_results || [],
      due_date: goal.due_date || '',
      priority_id: goal.priority_id || '',
    });
    setEditingGoal(goal);
    setShowForm(true);
  };

  const formatDueDate = (dateStr) => {
    if (!dateStr) return null;
    // Parse as local date to avoid timezone shift
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isDueSoon = (dateStr) => {
    if (!dateStr) return false;
    // Parse as local date to avoid timezone shift
    const [year, month, day] = dateStr.split('-').map(Number);
    const dueDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
    return diffDays <= 7 && diffDays >= 0;
  };

  const isOverdue = (dateStr) => {
    if (!dateStr) return false;
    // Parse as local date to avoid timezone shift
    const [year, month, day] = dateStr.split('-').map(Number);
    const dueDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  const categories = ['business', 'personal', 'health', 'learning', 'financial', 'team'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-slate-200 dark:border-[#1F2937] border-t-[#26C6DA] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="goals-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
            Strategic Goals
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Set ambitious objectives and track your action items to achieve them.
          </p>
        </div>
        <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] hover:from-[#0891B2] hover:to-[#7C3AED] text-black font-medium rounded-full px-6 gap-2" data-testid="create-goal-btn">
              <Plus className="w-4 h-4" />
              Create Goal
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-[#111827] border-slate-200 dark:border-[#1F2937]">
            <DialogHeader>
              <DialogTitle className="text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
                {editingGoal ? 'Edit Goal' : 'Create New Goal'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5 mt-4">
              <div className="space-y-2">
                <Label className="text-slate-600 dark:text-slate-300">Goal Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Launch successful product"
                  required
                  className="bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100 placeholder:text-slate-500"
                  data-testid="goal-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-600 dark:text-slate-300">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe your objective..."
                  className="bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100 placeholder:text-slate-500"
                  data-testid="goal-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-600 dark:text-slate-300">Category</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger className="bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100" data-testid="goal-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-[#111827] border-slate-200 dark:border-[#1F2937]">
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat} className="capitalize text-slate-800 dark:text-slate-100">{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600 dark:text-slate-300">Goal Due Date</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                    className="bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100"
                    data-testid="goal-duedate-input"
                  />
                </div>
              </div>

              {/* Strategic Priority Selection - Enterprise Only */}
              {hasEnterpriseAccess && priorities.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-slate-600 dark:text-slate-300 flex items-center gap-2">
                    <Flag className="w-4 h-4 text-purple-400" />
                    Strategic Priority
                    <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded-full">Enterprise</span>
                  </Label>
                  <Select 
                    value={formData.priority_id || ''} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, priority_id: value === 'none' ? '' : value }))}
                  >
                    <SelectTrigger className="bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100" data-testid="goal-priority-select">
                      <SelectValue placeholder="Select a priority (optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-[#111827] border-slate-200 dark:border-[#1F2937]">
                      <SelectItem value="none" className="text-slate-500 dark:text-slate-400">No priority</SelectItem>
                      {priorities.map(priority => (
                        <SelectItem key={priority.id} value={priority.id} className="text-slate-800 dark:text-slate-100">
                          {priority.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-600 dark:text-slate-300">Action Items</Label>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    onClick={addActionItem}
                    className="text-[#26C6DA] hover:text-[#26C6DA] hover:bg-[#26C6DA]/10"
                    data-testid="add-action-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Action Item
                  </Button>
                </div>
                {formData.key_results.map((item, index) => (
                  <div key={item.id} className="p-4 bg-slate-100 dark:bg-[#1A1A2E] rounded-xl space-y-3 border border-slate-200 dark:border-[#1F2937]">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Action Item {index + 1}</span>
                      {formData.key_results.length > 1 && (
                        <button 
                          type="button" 
                          onClick={() => removeActionItem(index)}
                          className="text-red-400 hover:text-red-300"
                          data-testid={`remove-action-${index}-btn`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <Input
                      value={item.title}
                      onChange={(e) => updateActionItem(index, 'title', e.target.value)}
                      placeholder="e.g., Complete market research"
                      className="bg-[#0A0A0A] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100 placeholder:text-slate-500"
                      data-testid={`action-title-${index}-input`}
                    />
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Due Date</Label>
                        <Input
                          type="date"
                          value={item.due_date || ''}
                          onChange={(e) => updateActionItem(index, 'due_date', e.target.value)}
                          className="bg-[#0A0A0A] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100"
                          data-testid={`action-due-${index}-input`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Repeat</Label>
                        <Select 
                          value={item.recurring || 'none'} 
                          onValueChange={(value) => updateActionItem(index, 'recurring', value === 'none' ? '' : value)}
                        >
                          <SelectTrigger className="bg-[#0A0A0A] border-slate-200 dark:border-[#1F2937] h-10 text-slate-800 dark:text-slate-100" data-testid={`action-recurring-${index}-select`}>
                            <SelectValue placeholder="None" />
                          </SelectTrigger>
                          <SelectContent className="bg-white dark:bg-[#111827] border-slate-200 dark:border-[#1F2937]">
                            <SelectItem value="none" className="text-slate-800 dark:text-slate-100">None</SelectItem>
                            <SelectItem value="daily" className="text-slate-800 dark:text-slate-100">Daily</SelectItem>
                            <SelectItem value="weekly" className="text-slate-800 dark:text-slate-100">Weekly</SelectItem>
                            <SelectItem value="monthly" className="text-slate-800 dark:text-slate-100">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Target</Label>
                        <Input
                          type="number"
                          value={item.target_value}
                          onChange={(e) => updateActionItem(index, 'target_value', parseFloat(e.target.value))}
                          placeholder="100"
                          className="bg-[#0A0A0A] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100"
                          data-testid={`action-target-${index}-input`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Unit</Label>
                        <Input
                          value={item.unit}
                          onChange={(e) => updateActionItem(index, 'unit', e.target.value)}
                          placeholder="%"
                          className="bg-[#0A0A0A] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100"
                          data-testid={`action-unit-${index}-input`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => { setShowForm(false); resetForm(); }}
                  className="rounded-full border-slate-200 dark:border-[#1F2937] text-slate-600 dark:text-slate-300 hover:bg-[#1F2937]"
                  data-testid="cancel-goal-btn"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] hover:from-[#0891B2] hover:to-[#7C3AED] text-black font-medium rounded-full"
                  data-testid="save-goal-btn"
                >
                  {editingGoal ? 'Update Goal' : 'Create Goal'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Date Warning Banner */}
      {goalsWithWarnings.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center justify-between" data-testid="date-warning-banner">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            <div>
              <p className="font-medium text-amber-400">
                {goalsWithWarnings.length} goal(s) have due dates before their action items
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Goal due dates should be on or after the latest action item date
              </p>
            </div>
          </div>
          <Button
            onClick={handleFixDates}
            disabled={fixingDates}
            className="bg-amber-500 hover:bg-amber-600 text-white rounded-full"
            data-testid="fix-dates-btn"
          >
            {fixingDates ? (
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Wrench className="w-4 h-4 mr-2" />
            )}
            Fix All Dates
          </Button>
        </div>
      )}

      {/* Goals List */}
      {goals.length > 0 ? (
        <div className="space-y-4">
          {goals.map((goal, index) => (
            <div 
              key={goal.id}
              className={`bg-white dark:bg-[#111827] backdrop-blur-md border rounded-2xl overflow-hidden transition-colors ${
                goal.date_warning 
                  ? 'border-amber-500/50 hover:border-amber-500' 
                  : 'border-slate-200 dark:border-[#1F2937] hover:border-[#26C6DA]/30'
              }`}
              data-testid={`goal-card-${index}`}
            >
              <div 
                className="p-6 cursor-pointer"
                onClick={() => setExpandedGoal(expandedGoal === goal.id ? null : goal.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                        goal.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                        goal.status === 'active' ? 'bg-[#26C6DA]/20 text-[#26C6DA]' :
                        'bg-slate-600/30 text-slate-500 dark:text-slate-400'
                      }`}>
                        {goal.status}
                      </span>
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#8B5CF6]/20 text-[#8B5CF6] capitalize">
                        {goal.category}
                      </span>
                      {goal.priority_id && priorities.find(p => p.id === goal.priority_id) && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400 flex items-center gap-1">
                          <Flag className="w-3 h-3" />
                          {priorities.find(p => p.id === goal.priority_id)?.title}
                        </span>
                      )}
                      {goal.due_date && (
                        <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                          goal.date_warning ? 'bg-amber-500/20 text-amber-400' :
                          isOverdue(goal.due_date) ? 'bg-red-500/20 text-red-400' :
                          isDueSoon(goal.due_date) ? 'bg-amber-500/20 text-amber-400' :
                          'bg-slate-600/30 text-slate-500 dark:text-slate-400'
                        }`}>
                          {goal.date_warning ? (
                            <AlertTriangle className="w-3 h-3" />
                          ) : (
                            <Calendar className="w-3 h-3" />
                          )}
                          {formatDueDate(goal.due_date)}
                          {goal.date_warning && goal.suggested_due_date && (
                            <span className="ml-1">→ {formatDueDate(goal.suggested_due_date)}</span>
                          )}
                        </span>
                      )}
                    </div>
                    <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
                      {goal.title}
                    </h3>
                    {goal.description && (
                      <p className="text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">{goal.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-[#26C6DA] font-mono">
                        {calculateGoalProgress(goal.key_results)}%
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Progress</p>
                    </div>
                    {expandedGoal === goal.id ? (
                      <ChevronUp className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                    )}
                  </div>
                </div>
                <div className="h-2 bg-[#1F2937] rounded-full overflow-hidden mt-4">
                  <div 
                    className="h-full bg-[#26C6DA] rounded-full transition-all"
                    style={{ width: `${Math.min(calculateGoalProgress(goal.key_results), 100)}%` }}
                  />
                </div>
              </div>

              {/* Expanded Content */}
              {expandedGoal === goal.id && (
                <div className="px-6 pb-6 border-t border-slate-200 dark:border-[#1F2937] pt-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-800 dark:text-slate-100">Action Items</span>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openEditForm(goal)}
                        className="text-slate-500 dark:text-slate-400 hover:text-[#26C6DA] hover:bg-[#26C6DA]/10"
                        data-testid={`edit-goal-${index}-btn`}
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDelete(goal.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        data-testid={`delete-goal-${index}-btn`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                  
                  {/* Action Items Table */}
                  <div className="bg-slate-100 dark:bg-[#1A1A2E] rounded-xl overflow-hidden border border-slate-200 dark:border-[#1F2937]">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200 dark:border-[#1F2937]">
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">Activity</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400 w-32">Due Date</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400 w-36">% Complete</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400 w-24">Update</th>
                        </tr>
                      </thead>
                      <tbody>
                        {goal.key_results?.map((item, itemIndex) => {
                          const progress = item.target_value > 0 
                            ? Math.round((item.current_value / item.target_value) * 100) 
                            : 0;
                          return (
                            <tr key={item.id} className="border-b border-slate-200 dark:border-[#1F2937] last:border-0">
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  {progress >= 100 ? (
                                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                                  ) : (
                                    <Circle className="w-4 h-4 text-slate-500 flex-shrink-0" />
                                  )}
                                  <span className={`font-medium ${progress >= 100 ? 'text-slate-500 line-through' : 'text-slate-800 dark:text-slate-100'}`}>
                                    {item.title || `Action Item ${itemIndex + 1}`}
                                  </span>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                {item.due_date ? (
                                  <span className={`text-sm flex items-center gap-1 ${
                                    isOverdue(item.due_date) && progress < 100 ? 'text-red-400' :
                                    isDueSoon(item.due_date) && progress < 100 ? 'text-amber-400' :
                                    'text-slate-500 dark:text-slate-400'
                                  }`}>
                                    <Clock className="w-3 h-3" />
                                    {formatDueDate(item.due_date)}
                                  </span>
                                ) : (
                                  <span className="text-slate-500 text-sm">No date</span>
                                )}
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 h-2 bg-[#1F2937] rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full rounded-full transition-all ${
                                        progress >= 100 ? 'bg-emerald-400' :
                                        progress >= 50 ? 'bg-[#26C6DA]' :
                                        'bg-slate-500'
                                      }`}
                                      style={{ width: `${Math.min(progress, 100)}%` }}
                                    />
                                  </div>
                                  <span className={`text-sm font-medium w-12 text-right ${
                                    progress >= 100 ? 'text-emerald-400' :
                                    progress >= 50 ? 'text-[#26C6DA]' :
                                    'text-slate-500 dark:text-slate-400'
                                  }`}>
                                    {progress}%
                                  </span>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <Input
                                  type="number"
                                  min="0"
                                  max={item.target_value}
                                  value={item.current_value}
                                  onChange={(e) => handleActionItemUpdate(goal.id, item.id, e.target.value)}
                                  className="w-20 h-8 bg-[#0A0A0A] border-slate-200 dark:border-[#1F2937] text-center text-sm text-slate-800 dark:text-slate-100"
                                  data-testid={`action-value-${itemIndex}-input`}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex items-center gap-3 pt-2">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Status:</span>
                    <Select 
                      value={goal.status} 
                      onValueChange={(value) => handleStatusChange(goal.id, value)}
                    >
                      <SelectTrigger className="w-40 bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100" data-testid={`goal-status-${index}-select`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white dark:bg-[#111827] border-slate-200 dark:border-[#1F2937]">
                        <SelectItem value="active" className="text-slate-800 dark:text-slate-100">Active</SelectItem>
                        <SelectItem value="in_progress" className="text-slate-800 dark:text-slate-100">In Progress</SelectItem>
                        <SelectItem value="completed" className="text-slate-800 dark:text-slate-100">Completed</SelectItem>
                        <SelectItem value="paused" className="text-slate-800 dark:text-slate-100">Paused</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-12 text-center">
          <Target className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Outfit' }}>
            No Goals Yet
          </h3>
          <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
            Start by creating your first strategic goal with action items to track your progress.
          </p>
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] hover:from-[#0891B2] hover:to-[#7C3AED] text-black font-medium rounded-full px-6 gap-2"
            data-testid="empty-create-goal-btn"
          >
            <Plus className="w-4 h-4" />
            Create Your First Goal
          </Button>
        </div>
      )}
    </div>
  );
};

export default Goals;
